import { useRef, useState } from 'react';
import LocalizedLink from '@/components/LocalizedLink';
import { trackEvent } from '@/lib/analytics';
import BookingLink, { BookingText } from '@/components/BookingLink';

// Same arithmetic as /roi-calculator (hours x people x hourly cost x 52). The
// presets only pre-fill the sliders; every number is the visitor's own
// assumption, so the result is an estimate of their cost, never a benchmark.
interface Inputs {
  hours: number;
  people: number;
  rate: number;
  share: number;
}

const DEFAULTS: Inputs = { hours: 6, people: 2, rate: 35, share: 40 };

const PRESETS: { label: string; values: Partial<Inputs> }[] = [
  { label: 'Lead follow-up', values: { hours: 6, people: 3, rate: 40 } },
  { label: 'Invoice & data entry', values: { hours: 10, people: 2, rate: 32 } },
  { label: 'Support triage', values: { hours: 12, people: 2, rate: 30 } },
  { label: 'Booking & reminders', values: { hours: 5, people: 2, rate: 28 } },
];

const SHARES = [25, 40, 60];

const fmt = (n: number) =>
  n.toLocaleString('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

const CostMeter = () => {
  const [v, setV] = useState<Inputs>(DEFAULTS);
  const touched = useRef(false);

  const annualCost = v.hours * v.people * v.rate * 52;
  const reclaim = annualCost * (v.share / 100);
  const hoursBack = v.hours * v.people * (v.share / 100);

  const update = (patch: Partial<Inputs>) => {
    setV((prev) => ({ ...prev, ...patch }));
    if (!touched.current) {
      touched.current = true;
      trackEvent('cost_meter_interact', { cta_location: 'homepage-cost-meter' });
    }
  };

  const slider = (
    key: 'hours' | 'people' | 'rate',
    label: string,
    min: number,
    max: number,
    display: string,
  ) => (
    <div className="cost-meter__field">
      <label htmlFor={`cm-${key}`}>
        <span>{label}</span>
        <strong>{display}</strong>
      </label>
      <input
        id={`cm-${key}`}
        type="range"
        min={min}
        max={max}
        step={1}
        value={v[key]}
        onChange={(e) => update({ [key]: Number(e.target.value) })}
      />
    </div>
  );

  return (
    <section className="section cost-meter" id="cost-meter" aria-labelledby="cost-meter-title">
      <div className="container">
        <div className="section-label">Try it in 10 seconds</div>
        <h2 id="cost-meter-title" className="cost-meter__title">
          What is one repetitive task costing you each year?
        </h2>
        <p className="section-intro">
          Pick a common task or set your own numbers. No sign-up, nothing is stored.
        </p>

        <div className="cost-meter__panel">
          <div className="cost-meter__inputs">
            <div className="cost-meter__chips" role="group" aria-label="Start from a common task">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className="cost-meter__chip"
                  onClick={() => update(p.values)}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {slider('hours', 'Hours a week, per person', 1, 40, `${v.hours} h`)}
            {slider('people', 'People doing it', 1, 20, String(v.people))}
            {slider('rate', 'Cost per hour', 15, 120, `${fmt(v.rate)}`)}
            <div className="cost-meter__field">
              <span id="cm-share-label" className="cost-meter__share-label">
                Share of it automation could take over
              </span>
              <div className="cost-meter__chips" role="group" aria-labelledby="cm-share-label">
                {SHARES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={'cost-meter__chip' + (v.share === s ? ' is-active' : '')}
                    aria-pressed={v.share === s}
                    onClick={() => update({ share: s })}
                  >
                    {s}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="cost-meter__result" aria-live="polite">
            <div className="cost-meter__label">That task costs about</div>
            <div className="cost-meter__big">
              {fmt(annualCost)}
              <span> / year</span>
            </div>
            <div className="cost-meter__reclaim">
              Automating {v.share}% would give back about <strong>{fmt(reclaim)}</strong> a year, or{' '}
              <strong>{hoursBack.toFixed(1)} hours</strong> a week.
            </div>
            <div className="cost-meter__actions">
              <BookingLink
                location="homepage-cost-meter"
                className="btn btn-primary"
                onClick={() =>
                  trackEvent('cta_click', {
                    cta_label: 'Cost meter: Book a Free Consultation',
                    cta_location: 'homepage-cost-meter',
                    annual_cost: annualCost,
                  })
                }
              >
                <BookingText /> <span aria-hidden="true">→</span>
              </BookingLink>
              <LocalizedLink
                to="/roi-calculator"
                className="btn btn-secondary"
                onClick={() =>
                  trackEvent('cta_click', {
                    cta_label: 'Cost meter: full calculator',
                    cta_location: 'homepage-cost-meter',
                  })
                }
              >
                See payback and 3-year view
              </LocalizedLink>
            </div>
            <p className="cost-meter__note">
              Arithmetic on your own inputs: hours × people × cost per hour × 52 weeks. An estimate, not a
              guarantee of savings.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostMeter;
